/**
 * =============================================================================
 * App Detail Page
 * =============================================================================
 *
 * Detailed view of an app with all its information and activity history.
 *
 * Features:
 * - App information display
 * - Parameters table
 * - Requirements display
 * - Activity timeline (reviews, approvals, version changes)
 * - Version history
 * - Actions based on role and app status
 *
 * Two independent states (BE issue #52):
 * - APPROVAL WORKFLOW (`status`): approve / reject. 'rejected' means "we are
 *   not releasing this version" — and it is not a dead end: an admin can still
 *   approve it, the timeline is appended to, never rewritten.
 * - OPERATIONAL SWITCH (`is_active`): deactivate / activate. A deactivated app
 *   disappears from devices (device-api gates on the same flag) while its
 *   approval stays valid, so activation is instant.
 *
 * Workflow:
 * - DEV/TEST: Apps and new versions are auto-approved
 * - PROD: Requires admin approval (via "Move to PROD" action)
 *
 * Access: system_admin, app_admin, app_dev
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { appsApi, AppDetail, AppReview, PromotionCheck, PromotionBlockedDetail } from '../services/api'
import { useAuth } from '../auth/AuthProvider'
import { shortDigest } from '../utils/imageRef'
import { pt, checkMessage, getLang } from '../utils/promotionI18n'
import { at, atBoth } from '../utils/appAdminI18n'
import Modal from '../components/Modal'
import AppEditModal from '../components/AppEditModal'
import YamlViewer from '../components/YamlViewer'

/**
 * Backend answer when a delete is refused because the app is referenced
 * (BE: 409 `app_in_use`). Rendered as a plain-language explanation — FE #24.
 */
interface DeleteBlockedDetail {
  code?: string
  message?: string
  rehab_plan_items?: number
  patient_records?: number
  hand_metrics?: number
}

// Helper to convert multi-language values to string
const toDisplayString = (value: string | Record<string, string> | undefined | null): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value.en || value.sk || Object.values(value)[0] || ''
  }
  return String(value)
}

// =============================================================================
// Status Badge Component
// =============================================================================

interface StatusBadgeProps {
  status: string
  large?: boolean
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, large = false }) => {
  const statusConfig: Record<string, { className: string; label: string }> = {
    draft: { className: 'bg-gray-100 text-gray-800', label: at('statusDraft') },
    pending_approval: { className: 'bg-yellow-100 text-yellow-800', label: at('statusPending') },
    approved: { className: 'bg-green-100 text-green-800', label: at('statusApproved') },
    rejected: { className: 'bg-red-100 text-red-800', label: at('statusRejected') },
    deprecated: { className: 'bg-gray-100 text-gray-600', label: at('statusDeprecated') },
  }

  const config = statusConfig[status] || { className: 'bg-gray-100 text-gray-800', label: status }

  return (
    <span className={`badge ${config.className} ${large ? 'text-base px-4 py-1' : ''}`}>
      {config.label}
    </span>
  )
}

/**
 * Operational state badge (issue #52) — deliberately SEPARATE from StatusBadge.
 * The approval status and the on/off switch are two different things, so they
 * are two different badges; an app can be "approved" and "deactivated" at once.
 */
const DeactivatedBadge: React.FC<{ large?: boolean }> = ({ large = false }) => (
  <span
    className={`badge bg-orange-100 text-orange-800 ${large ? 'text-base px-4 py-1' : ''}`}
    title={atBoth('deactivatedBannerTitle')}
    data-testid="app-deactivated-badge"
  >
    {at('deactivatedBadge')}
  </span>
)

// =============================================================================
// Activity Timeline Component
// =============================================================================

interface ActivityTimelineProps {
  reviews: AppReview[]
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ reviews }) => {
  if (reviews.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No activity recorded yet.
      </div>
    )
  }

  const getStatus = (review: AppReview): string => {
    return review.status || review.review_type || 'comment'
  }

  const getActivityIcon = (status: string): string => {
    switch (status) {
      case 'approved':
      case 'approval':
        return '✓'
      case 'rejected':
      case 'rejection':
        return '✕'
      case 'request_changes':
      case 'changes_requested':
        return '!'
      default:
        return '💬'
    }
  }

  const getActivityColor = (status: string): string => {
    switch (status) {
      case 'approved':
      case 'approval':
        return 'bg-green-500'
      case 'rejected':
      case 'rejection':
        return 'bg-red-500'
      case 'request_changes':
      case 'changes_requested':
        return 'bg-yellow-500'
      default:
        return 'bg-blue-500'
    }
  }

  const getActionLabel = (status: string): { text: string; badge: string } => {
    switch (status) {
      case 'approved':
      case 'approval':
        return { text: 'APPROVED', badge: 'bg-green-100 text-green-800' }
      case 'rejected':
      case 'rejection':
        return { text: 'REJECTED', badge: 'bg-red-100 text-red-800' }
      case 'request_changes':
      case 'changes_requested':
        return { text: 'CHANGES REQUESTED', badge: 'bg-yellow-100 text-yellow-800' }
      default:
        return { text: 'ACTIVITY', badge: 'bg-blue-100 text-blue-800' }
    }
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => {
        const status = getStatus(review)
        const actionLabel = getActionLabel(status)

        return (
          <div key={review.id} className="flex gap-4">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full ${getActivityColor(status)} text-white flex items-center justify-center text-sm font-bold`}>
              {getActivityIcon(status)}
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`badge text-xs font-semibold ${actionLabel.badge}`}>
                    {actionLabel.text}
                  </span>
                  {review.version_number && (
                    <span className="badge bg-gray-200 text-gray-600 text-xs">
                      v{review.version_number}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(review.created_at)}</span>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                by <span className="font-medium text-gray-800">{review.reviewer_name || 'System'}</span>
              </div>
              {review.comment && (
                <p className="text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border border-gray-200">
                  {review.comment}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// =============================================================================
// Parameters Table Component
// =============================================================================

interface ParametersTableProps {
  parameters: AppDetail['parameters']
}

const ParametersTable: React.FC<ParametersTableProps> = ({ parameters }) => {
  if (!parameters || parameters.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        No parameters defined.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left bg-gray-50">
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Scope</th>
            <th className="px-4 py-2 font-medium">Type</th>
            <th className="px-4 py-2 font-medium">Default</th>
            <th className="px-4 py-2 font-medium">Range</th>
            <th className="px-4 py-2 font-medium">Unit</th>
            <th className="px-4 py-2 font-medium">Doctor Configurable</th>
            <th className="px-4 py-2 font-medium">Visibility</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {parameters.map((param, idx) => (
            <tr key={idx}>
              <td className="px-4 py-2">
                <div className="font-mono text-xs">{param.name}</div>
                {param.description && (
                  <div className="text-xs text-gray-500 mt-0.5">{toDisplayString(param.description)}</div>
                )}
              </td>
              <td className="px-4 py-2">
                {param.scope ? (
                  <span className="badge bg-blue-50 text-blue-700">{param.scope}</span>
                ) : '-'}
              </td>
              <td className="px-4 py-2">
                <span className="badge bg-gray-100 text-gray-800">{param.type}</span>
                {param.values && param.values.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    [{param.values.join(', ')}]
                  </div>
                )}
              </td>
              <td className="px-4 py-2 font-mono">{String(param.default ?? '-')}</td>
              <td className="px-4 py-2">
                {param.min !== undefined && param.max !== undefined
                  ? `${param.min} - ${param.max}`
                  : param.min !== undefined
                  ? `≥ ${param.min}`
                  : param.max !== undefined
                  ? `≤ ${param.max}`
                  : '-'}
              </td>
              <td className="px-4 py-2">{toDisplayString(param.unit) || '-'}</td>
              <td className="px-4 py-2">
                {param.doctor_configurable ? (
                  <span className="text-green-600 font-medium">Yes</span>
                ) : (
                  <span className="text-gray-400">No</span>
                )}
              </td>
              <td className="px-4 py-2">
                {param.doctor_visibility || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// =============================================================================
// Reject Modal Component
// =============================================================================

interface RejectModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (reason: string, detailedDescription?: string) => void
  isSubmitting: boolean
  appName: string
}

const RejectModal: React.FC<RejectModalProps> = ({ isOpen, onClose, onSubmit, isSubmitting, appName }) => {
  const [reason, setReason] = useState('')
  const [detailedDescription, setDetailedDescription] = useState('')

  useEffect(() => {
    if (isOpen) {
      setReason('')
      setDetailedDescription('')
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(reason, detailedDescription || undefined)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={at('rejectTitle')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-red-800 font-medium mb-2">{at('rejectIntroTitle')}</h4>
          <p className="text-red-700 text-sm">
            <strong>"{appName}"</strong> — {at('rejectIntroBody')}
          </p>
          {/* issue #52: say out loud which of the two actions this is */}
          <p className="text-red-600 text-sm mt-2">{at('rejectNotDeactivate')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="reject-reason">
            {at('rejectReasonLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            id="reject-reason"
            type="text"
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={at('rejectReasonPlaceholder')}
            maxLength={200}
            required
          />
          <p className="text-xs text-gray-500 mt-1">{at('rejectReasonHint')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="reject-detail">
            {at('rejectDetailLabel')}
          </label>
          <textarea
            id="reject-detail"
            className="input min-h-[200px] font-mono text-sm"
            value={detailedDescription}
            onChange={(e) => setDetailedDescription(e.target.value)}
            placeholder={at('rejectDetailPlaceholder')}
            rows={8}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            {at('cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary bg-red-600 hover:bg-red-700"
            disabled={isSubmitting || !reason.trim()}
          >
            {isSubmitting ? at('rejectSubmitting') : at('rejectSubmit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// =============================================================================
// Comment Modal Component
// =============================================================================

interface CommentModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (comment: string, type: string) => void
  isSubmitting: boolean
  isAdmin: boolean
  appName: string
}

const CommentModal: React.FC<CommentModalProps> = ({ isOpen, onClose, onSubmit, isSubmitting, isAdmin, appName }) => {
  const [comment, setComment] = useState('')
  const [type, setType] = useState('comment')

  useEffect(() => {
    if (isOpen) {
      setComment('')
      setType('comment')
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(comment, type)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Comment" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-blue-700 text-sm">
            Adding comment to <strong>"{appName}"</strong>.
            Your name will be displayed with this comment.
          </p>
        </div>

        {isAdmin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comment Type
            </label>
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="comment">General Comment</option>
              <option value="request_changes">Request Changes</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Comment <span className="text-red-500">*</span>
          </label>
          <textarea
            className="input min-h-[150px]"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Write your comment here..."
            rows={6}
            required
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || !comment.trim()}
          >
            {isSubmitting ? 'Submitting...' : 'Add Comment'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// =============================================================================
// Approve Modal Component
// =============================================================================

interface ApproveModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (comment?: string) => void
  isSubmitting: boolean
  appName: string
  /**
   * Issue #52: the app is currently 'rejected'. The portal used to hide the
   * approve button in that state, so the only way back was editing the image.
   * Now it is offered — with wording that says plainly what is happening and
   * that the rejection stays in the history.
   */
  wasRejected?: boolean
  /**
   * Issue #52: approving does NOT switch a deactivated app back on. Say so
   * here, otherwise "approved" reads as "live on devices".
   */
  isActive?: boolean
}

const ApproveModal: React.FC<ApproveModalProps> = ({
  isOpen, onClose, onSubmit, isSubmitting, appName, wasRejected = false, isActive = true,
}) => {
  const [comment, setComment] = useState('')

  useEffect(() => {
    if (isOpen) {
      setComment('')
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(comment || undefined)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={wasRejected ? at('approveRejectedAction') : at('approveTitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {wasRejected && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="approve-rejected-notice">
            <h4 className="text-amber-900 font-medium mb-2">{at('approveRejectedTitle')}</h4>
            <p className="text-amber-800 text-sm">{at('approveRejectedBody')}</p>
          </div>
        )}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-green-800 font-medium mb-2">{at('approveIntroTitle')}</h4>
          <p className="text-green-700 text-sm">
            <strong>"{appName}"</strong> — {at('approveIntroBody')}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-blue-800 font-medium mb-2">{at('approveEffectsTitle')}</h4>
          <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
            <li>{at('approveEffectDoctor')}</li>
            <li>{at('approveEffectAssign')}</li>
            <li>{at('approveEffectDevices')}</li>
          </ul>
          {/* the operational switch outranks the approval on the device gate */}
          {!isActive && (
            <p className="text-blue-900 text-sm mt-2 font-medium" data-testid="approve-deactivated-note">
              {at('approveEffectDeactivated')}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="approve-comment">
            {at('approveCommentLabel')}
          </label>
          <textarea
            id="approve-comment"
            className="input min-h-[80px]"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={at('approveCommentPlaceholder')}
            rows={3}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            {at('cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary bg-green-600 hover:bg-green-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? at('approveSubmitting') : at('approveSubmit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
// =============================================================================
// Activation Modal (BE issue #52) — the OPERATIONAL switch, not a verdict
// =============================================================================

interface ActivationModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'deactivate' | 'activate'
  appName: string
  isSubmitting: boolean
  error: string | null
  onSubmit: (comment?: string) => void
}

/**
 * Confirmation dialog for switching an app off / back on.
 *
 * Deliberately worded so nobody confuses it with a rejection: the approval and
 * the approved digest survive a deactivation, which is exactly why activation
 * is instant. All texts SK+EN via appAdminI18n.
 */
const ActivationModal: React.FC<ActivationModalProps> = ({
  isOpen, onClose, mode, appName, isSubmitting, error, onSubmit,
}) => {
  const [comment, setComment] = useState('')
  const deactivating = mode === 'deactivate'

  useEffect(() => {
    if (isOpen) setComment('')
  }, [isOpen, mode])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(comment.trim() || undefined)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={deactivating ? at('deactivateTitle') : at('activateTitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div
          className={`${deactivating ? 'bg-orange-50 border-orange-300' : 'bg-green-50 border-green-300'} border rounded-lg p-4`}
        >
          <h4 className={`${deactivating ? 'text-orange-900' : 'text-green-900'} font-medium mb-2`}>
            {deactivating ? at('deactivateWarnTitle') : at('activateWarnTitle')}
          </h4>
          <p className={`${deactivating ? 'text-orange-800' : 'text-green-800'} text-sm`}>
            {deactivating ? at('deactivateWarnBody') : at('activateWarnBody')}
          </p>
          <p className="text-gray-600 text-sm mt-2">
            <strong>{appName}</strong>
          </p>
        </div>

        <div>
          <label
            className="block text-sm font-medium text-gray-700 mb-1"
            htmlFor="activation-comment"
          >
            {deactivating ? at('deactivateReasonLabel') : pt('commentLabel')}
          </label>
          <textarea
            id="activation-comment"
            className="input min-h-[80px]"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={deactivating ? at('deactivateReasonPlaceholder') : pt('commentPlaceholder')}
            rows={3}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            {at('cancel')}
          </button>
          <button
            type="submit"
            className={`btn-primary ${deactivating ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? pt('working')
              : deactivating ? at('confirmDeactivate') : at('confirmActivate')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// =============================================================================
// Promotion Modal (FE issue #23 / BE issue #49) — dev/test -> prod and back
// =============================================================================

interface PromotionModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'promote' | 'demote'
  appId: string
  appName: string
  onChanged: () => void
}

interface PromotionOutcome {
  ok: boolean
  message: string
  checks: PromotionCheck[]
}

/**
 * Confirmation dialog for moving an app to production (or back to dev).
 * Shows the BE validation result — the full check list — on both success
 * and 409 rejection. All texts SK+EN via promotionI18n.
 */
const PromotionModal: React.FC<PromotionModalProps> = ({
  isOpen, onClose, mode, appId, appName, onChanged,
}) => {
  const [comment, setComment] = useState('')
  const [outcome, setOutcome] = useState<PromotionOutcome | null>(null)

  useEffect(() => {
    if (isOpen) {
      setComment('')
      setOutcome(null)
    }
  }, [isOpen, mode])

  const mutation = useMutation({
    mutationFn: () =>
      mode === 'promote' ? appsApi.promote(appId, comment || undefined)
                         : appsApi.demote(appId, comment || undefined),
    onSuccess: (res) => {
      setOutcome({
        ok: true,
        message: pt(mode === 'promote' ? 'promotedOk' : 'demotedOk'),
        checks: res.data.checks || [],
      })
      onChanged()
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: PromotionBlockedDetail | string } } })
        ?.response?.data?.detail
      if (detail && typeof detail === 'object') {
        const lang = getLang()
        setOutcome({
          ok: false,
          message: (lang === 'sk' ? detail.message_sk : detail.message) || pt('blocked'),
          checks: detail.checks || [],
        })
      } else {
        setOutcome({ ok: false, message: pt('requestFailed'), checks: [] })
      }
    },
  })

  const isPromote = mode === 'promote'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={pt(isPromote ? 'promoteTitle' : 'demoteTitle')}
      size="lg"
    >
      <div className="space-y-4">
        {outcome === null && (
          <>
            <div className={`border rounded-lg p-4 ${
              isPromote ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <h4 className={`font-medium mb-2 ${isPromote ? 'text-green-800' : 'text-yellow-800'}`}>
                {pt(isPromote ? 'promoteWarnTitle' : 'demoteWarnTitle')}
              </h4>
              <p className={`text-sm ${isPromote ? 'text-green-700' : 'text-yellow-700'}`}>
                <strong>"{appName}"</strong> — {pt(isPromote ? 'promoteWarnBody' : 'demoteWarnBody')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {pt('commentLabel')}
              </label>
              <textarea
                className="input min-h-[60px]"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={pt('commentPlaceholder')}
                rows={2}
              />
            </div>
          </>
        )}

        {outcome !== null && (
          <div className={`border rounded-lg p-4 ${
            outcome.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <h4 className={`font-medium mb-1 ${outcome.ok ? 'text-green-800' : 'text-red-800'}`}>
              {outcome.message}
            </h4>
          </div>
        )}

        {outcome !== null && outcome.checks.length > 0 && (
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{pt('checksTitle')}</h4>
            <ul className="space-y-1">
              {outcome.checks.map((c) => (
                <li key={c.check} className="flex items-start gap-2 text-sm">
                  <span className={c.passed ? 'text-green-600' : 'text-red-600'}>
                    {c.passed ? '✓' : '✕'}
                  </span>
                  <span className={c.passed ? 'text-gray-700' : 'text-red-700'}>
                    {checkMessage(c)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          {outcome === null ? (
            <>
              <button type="button" onClick={onClose} className="btn-secondary" disabled={mutation.isPending}>
                {pt('cancel')}
              </button>
              <button
                type="button"
                onClick={() => mutation.mutate()}
                className={`btn-primary ${
                  isPromote ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? pt('working') : pt(isPromote ? 'confirmPromote' : 'confirmDemote')}
              </button>
            </>
          ) : (
            <button type="button" onClick={onClose} className="btn-secondary">
              {pt('close')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

// =============================================================================
// Configuration Card (issue #20)
// =============================================================================

interface AppConfigCardProps {
  appId: string
  appSlug: string
}

/**
 * Stored app configuration: syntax-highlighted YAML preview + download.
 * Loads GET /apps/{id}/config lazily (separate from the detail payload).
 */
const AppConfigCard: React.FC<AppConfigCardProps> = ({ appId, appSlug }) => {
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['app-config', appId],
    queryFn: () => appsApi.getConfig(appId),
    enabled: !!appId,
  })

  const configYaml = config?.data?.config_yaml

  const handleDownload = () => {
    if (!configYaml) return
    const blob = new Blob([configYaml], { type: 'application/x-yaml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${appSlug || appId}-config.yaml`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Configuration</h2>
        <button
          onClick={handleDownload}
          className="btn-secondary text-sm"
          disabled={!configYaml}
          title={configYaml ? 'Download the stored YAML config file' : 'No stored configuration'}
        >
          &#8595; Download YAML
        </button>
      </div>

      {isLoading && <div className="text-gray-500 text-sm py-4">Loading configuration...</div>}
      {!!error && (
        <div className="text-red-500 text-sm py-4">Failed to load configuration</div>
      )}
      {!isLoading && !error && !configYaml && (
        <div className="text-gray-500 text-sm py-4">
          No configuration file is stored for this app.
        </div>
      )}
      {!isLoading && !error && configYaml && <YamlViewer yaml={configYaml} />}
    </div>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

const AppDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()

  // Modal states
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
  const [promotionMode, setPromotionMode] = useState<'promote' | 'demote' | null>(null)
  // Issue #52: the operational switch. `null` = dialog closed.
  const [activationMode, setActivationMode] = useState<'deactivate' | 'activate' | null>(null)
  const [activationError, setActivationError] = useState<string | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  // FE issue #24: a blocked delete used to be a silent 500. Keep the backend's
  // answer so the page can say *why* the app cannot be deleted.
  const [deleteBlocked, setDeleteBlocked] = useState<DeleteBlockedDetail | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [savedNotice, setSavedNotice] = useState<string | null>(null)

  // Fetch app detail
  const { data: app, isLoading, error } = useQuery({
    queryKey: ['app', id],
    queryFn: () => appsApi.get(id!),
    enabled: !!id,
  })

  // Fetch reviews/activity
  const { data: reviews } = useQuery({
    queryKey: ['app-reviews', id],
    queryFn: () => appsApi.getReviews(id!),
    enabled: !!id,
  })

  // Approve mutation (app-level)
  const approveMutation = useMutation({
    mutationFn: (comment?: string) => appsApi.approve(id!, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app', id] })
      queryClient.invalidateQueries({ queryKey: ['app-reviews', id] })
      queryClient.invalidateQueries({ queryKey: ['apps'] })
      setIsApproveModalOpen(false)
    },
  })

  // Reject mutation (app-level)
  const rejectMutation = useMutation({
    mutationFn: ({ reason, comment }: { reason: string; comment?: string }) =>
      appsApi.reject(id!, { reason, comment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app', id] })
      queryClient.invalidateQueries({ queryKey: ['app-reviews', id] })
      queryClient.invalidateQueries({ queryKey: ['apps'] })
      setIsRejectModalOpen(false)
    },
  })

  // Add review mutation
  const addReviewMutation = useMutation({
    mutationFn: (data: { comment: string; review_type: string }) => appsApi.addReview(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-reviews', id] })
      setIsCommentModalOpen(false)
    },
  })

  // Delete mutation — 409 means "in use" and is an expected answer, not a crash
  const deleteMutation = useMutation({
    mutationFn: () => appsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] })
      navigate('/apps')
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
        ?.response
      const detail = response?.data?.detail
      if (response?.status === 409 && detail && typeof detail === 'object') {
        setDeleteBlocked(detail as DeleteBlockedDetail)
        return
      }
      setDeleteError(typeof detail === 'string' ? detail : at('deleteFailed'))
    },
  })

  /**
   * Operational switch (issue #52). This used to call `appsApi.reject(...)`,
   * which meant "deactivating" an app actually REJECTED it — and, because the
   * approve button only rendered for 'pending_approval', there was no way back
   * in the UI. It now calls the real endpoints; the approval is untouched.
   */
  const activationMutation = useMutation({
    mutationFn: ({ mode, comment }: { mode: 'deactivate' | 'activate'; comment?: string }) =>
      mode === 'deactivate' ? appsApi.deactivate(id!, comment) : appsApi.activate(id!, comment),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['app', id] })
      queryClient.invalidateQueries({ queryKey: ['app-reviews', id] })
      queryClient.invalidateQueries({ queryKey: ['apps'] })
      setActivationMode(null)
      setActivationError(null)
      setDeleteBlocked(null)
      setSavedNotice(variables.mode === 'deactivate' ? at('deactivatedOk') : at('activatedOk'))
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      if (detail && typeof detail === 'object') {
        const d = detail as { message?: string; message_sk?: string }
        setActivationError((getLang() === 'sk' ? d.message_sk : d.message) || at('switchFailed'))
        return
      }
      setActivationError(typeof detail === 'string' ? detail : at('switchFailed'))
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (error || !app?.data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Failed to load app</div>
      </div>
    )
  }

  const appData = app.data
  const isAdmin = hasRole('system_admin') || hasRole('app_admin')
  const status = appData.status
  const isPendingApproval = status === 'pending_approval'
  const isRejected = status === 'rejected'
  // Operational switch (issue #52). Older backends do not send the field —
  // treat "unknown" as active, exactly like the device-api gate does.
  const isActive = appData.is_active !== false
  const canDelete = isAdmin  // Only admins can delete
  // app_dev may edit their own apps; the backend enforces team membership.
  const canEdit = isAdmin || hasRole('app_dev')

  // Admin can approve when the app is pending — and ALSO when it was rejected
  // (issue #52): a rejection is a verdict on a review, not a dead end. The
  // backend has always accepted this; only the button was missing.
  const canApprove = isAdmin && (isPendingApproval || isRejected)
  // Rejecting is only meaningful while the app is not already rejected.
  const canReject = isAdmin && isPendingApproval

  // Issue #50: dev apps are released automatically by the digest watcher, so
  // the "pending re-approval" banner never appears for them. Say so explicitly,
  // otherwise its absence looks like a bug.
  const autoApproved = appData.auto_approved === true

  // Image changed after approval -> needs re-approval
  const needsReapproval =
    isPendingApproval &&
    !!appData.approved_image_digest &&
    !!appData.current_image_digest &&
    appData.current_image_digest !== appData.approved_image_digest

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/apps')}
            className="text-gray-600 hover:text-gray-800 text-sm mb-4 flex items-center gap-1"
          >
            &larr; Back to Apps
          </button>
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">{appData.name}</h1>
            <StatusBadge status={status} large />
            {!isActive && <DeactivatedBadge large />}
            {appData.environment && (
              <span className={`badge ${
                appData.environment === 'prod' ? 'bg-green-100 text-green-800' :
                appData.environment === 'test' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {appData.environment.toUpperCase()}
              </span>
            )}
            {autoApproved && (
              <span
                className="badge bg-emerald-100 text-emerald-800"
                title={`${pt('autoApproveTitle', 'sk')} / ${pt('autoApproveTitle', 'en')}`}
              >
                {pt('autoApproveBadge')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1">
            <p className="text-gray-500 font-mono text-sm">
              <span className="text-gray-400">ID:</span> {appData.app_id}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {canApprove && (
            <button
              className="btn-primary bg-green-600 hover:bg-green-700"
              onClick={() => setIsApproveModalOpen(true)}
              title={isRejected ? atBoth('approveRejectedAction') : atBoth('approveAction')}
              data-testid="approve-button"
            >
              {isRejected ? at('approveRejectedAction') : at('approveAction')}
            </button>
          )}
          {canReject && (
            <button
              className="btn-primary bg-red-600 hover:bg-red-700"
              onClick={() => setIsRejectModalOpen(true)}
              title={atBoth('rejectAction')}
              data-testid="reject-button"
            >
              {at('rejectAction')}
            </button>
          )}
          {/* Operational switch (issue #52) — always available to admins, in
              any approval state, because it is a different question. */}
          {isAdmin && (
            <button
              className={`btn-primary ${isActive ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-700 hover:bg-green-800'}`}
              onClick={() => {
                setActivationError(null)
                setActivationMode(isActive ? 'deactivate' : 'activate')
              }}
              title={isActive ? atBoth('deactivateAction') : atBoth('activateAction')}
              data-testid="activation-toggle"
            >
              {isActive ? at('deactivateAction') : at('activateAction')}
            </button>
          )}
          {isAdmin && appData.environment !== 'prod' && (
            <button
              className="btn-primary bg-emerald-700 hover:bg-emerald-800"
              onClick={() => setPromotionMode('promote')}
              title={`${pt('promoteAction', 'sk')} / ${pt('promoteAction', 'en')}`}
            >
              {pt('promoteAction')}
            </button>
          )}
          {isAdmin && appData.environment === 'prod' && (
            <button
              className="btn-primary bg-yellow-600 hover:bg-yellow-700"
              onClick={() => setPromotionMode('demote')}
              title={`${pt('demoteAction', 'sk')} / ${pt('demoteAction', 'en')}`}
            >
              {pt('demoteAction')}
            </button>
          )}
          {isAdmin && (
            <button
              className="btn-secondary"
              onClick={() => setIsCommentModalOpen(true)}
            >
              Add Comment
            </button>
          )}
          {canEdit && (
            <button
              className="btn-primary"
              onClick={() => setIsEditModalOpen(true)}
              title={`${at('editAction', 'sk')} / ${at('editAction', 'en')}`}
            >
              {at('editAction')}
            </button>
          )}
          {canDelete && (
            <button
              className="btn-secondary text-red-600 hover:text-red-700"
              onClick={() => {
                setDeleteError(null)
                if (confirm(`${at('deleteConfirm', 'sk')}\n${at('deleteConfirm', 'en')}`)) {
                  deleteMutation.mutate()
                }
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? at('deleting') : at('deleteAction')}
            </button>
          )}
        </div>
      </div>

      {/* Saved notice after an edit (FE issue #25) */}
      {savedNotice && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" role="status">
          <p className="text-blue-800 text-sm">{savedNotice}</p>
        </div>
      )}

      {/* Delete refused: say why, in plain language, and offer a way out
          (FE issue #24 — this used to be an unhandled 500 and nothing else). */}
      {deleteBlocked && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4" role="alert">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-red-800 font-medium mb-1">{at('deleteBlockedTitle')}</h3>
              <p className="text-red-700 text-sm">{at('deleteBlockedWhy')}</p>
              <ul className="text-red-700 text-sm list-disc list-inside mt-2">
                {!!deleteBlocked.rehab_plan_items && (
                  <li>
                    {deleteBlocked.rehab_plan_items} {at('usedInPlans')}
                  </li>
                )}
                {!!deleteBlocked.patient_records && (
                  <li>
                    {deleteBlocked.patient_records} {at('usedInRecords')}
                  </li>
                )}
                {!!deleteBlocked.hand_metrics && (
                  <li>
                    {deleteBlocked.hand_metrics} {at('usedInMetrics')}
                  </li>
                )}
              </ul>
              <h4 className="text-red-800 font-medium mt-3 mb-1">
                {at('deleteAlternativesTitle')}
              </h4>
              <ul className="text-red-700 text-sm list-disc list-inside space-y-1">
                <li>{at('deleteAltRepoint')}</li>
                {appData.environment === 'prod' && <li>{at('deleteAltDemote')}</li>}
                {isActive && <li>{at('deleteAltDeactivate')}</li>}
              </ul>
              {isAdmin && isActive && (
                <button
                  className="btn-secondary mt-3"
                  onClick={() => {
                    setActivationError(null)
                    setActivationMode('deactivate')
                  }}
                  data-testid="delete-blocked-deactivate"
                >
                  {at('deactivateAction')}
                </button>
              )}
            </div>
            <button
              type="button"
              className="text-red-700 hover:text-red-900"
              onClick={() => setDeleteBlocked(null)}
              aria-label={at('close')}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {deleteError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
          <p className="text-red-700 text-sm">{deleteError}</p>
        </div>
      )}

      {/* Deactivated banner (issue #52) — says what it means and offers the
          way back in one click, because the approval is still valid. */}
      {!isActive && (
        <div className="bg-orange-50 border border-orange-300 rounded-lg p-4" role="status">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-orange-900 font-medium mb-1">{at('deactivatedBannerTitle')}</h3>
              <p className="text-orange-800 text-sm">{at('deactivatedBannerBody')}</p>
            </div>
            {isAdmin && (
              <button
                className="btn-primary bg-green-700 hover:bg-green-800 whitespace-nowrap"
                onClick={() => {
                  setActivationError(null)
                  setActivationMode('activate')
                }}
                data-testid="activate-from-banner"
              >
                {at('activateAction')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pending Re-approval Banner (image changed since approval) */}
      {needsReapproval && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-yellow-800 font-medium mb-1">Pending re-approval (image changed)</h3>
              <p className="text-yellow-700 text-sm mb-3">
                The container image was rebuilt after the last approval. Review the new
                image and re-approve to make it available again.
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <dt className="text-yellow-700">Approved digest</dt>
                  <dd className="font-mono text-yellow-900">{shortDigest(appData.approved_image_digest)}</dd>
                </div>
                <div>
                  <dt className="text-yellow-700">Current digest</dt>
                  <dd className="font-mono text-yellow-900">{shortDigest(appData.current_image_digest)}</dd>
                </div>
              </dl>
            </div>
            {isAdmin && (
              <button
                className="btn-primary bg-yellow-600 hover:bg-yellow-700 whitespace-nowrap"
                onClick={() => setIsApproveModalOpen(true)}
              >
                Re-approve
              </button>
            )}
          </div>
        </div>
      )}

      {/* Auto-approve notice for dev apps (issue #50) */}
      {autoApproved && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4">
          <h3 className="text-emerald-800 font-medium mb-1">{pt('autoApproveTitle')}</h3>
          <p className="text-emerald-700 text-sm">{pt('autoApproveBody')}</p>
          <p className="text-emerald-700 text-sm mt-2">{pt('autoApproveProdNote')}</p>
          <p className="text-emerald-600 text-xs mt-2">{pt('autoApproveDisableHint')}</p>
        </div>
      )}

      {/* Rejection Reason Alert */}
      {isRejected && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium mb-1">{at('rejectedBannerTitle')}</h3>
          {appData.review_comment && <p className="text-red-700">{appData.review_comment}</p>}
          <p className="text-red-600 text-sm mt-2">{at('rejectedBannerHint')}</p>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">App Information</h2>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-500">App ID</dt>
                <dd className="font-mono font-medium">{appData.app_id}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Category</dt>
                <dd className="capitalize">{appData.category || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Environment</dt>
                <dd className="uppercase font-medium">{appData.environment || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Team</dt>
                <dd className="font-medium">{appData.app_team_name || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Status</dt>
                <dd className="mt-1"><StatusBadge status={status} /></dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Current Digest</dt>
                <dd className="font-mono text-sm">{shortDigest(appData.current_image_digest) || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Approved Digest</dt>
                <dd className="font-mono text-sm">{shortDigest(appData.approved_image_digest) || '-'}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-sm text-gray-500">Docker Image</dt>
                <dd className="font-mono text-sm bg-gray-100 px-3 py-2 rounded mt-1 break-all">
                  {appData.docker_image_url || '-'}
                </dd>
              </div>
              {appData.description && (
                <div className="col-span-2">
                  <dt className="text-sm text-gray-500">Description</dt>
                  <dd className="mt-1">{appData.description}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Parameters Card */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Parameters
              {appData.parameters && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({appData.parameters.length})
                </span>
              )}
            </h2>
            <ParametersTable parameters={appData.parameters} />
          </div>

          {/* Result Schema Card */}
          {appData.result_schema && appData.result_schema.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Result Schema</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left bg-gray-50">
                      <th className="px-4 py-2 font-medium">Field</th>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <th className="px-4 py-2 font-medium">Required</th>
                      <th className="px-4 py-2 font-medium">Unit</th>
                      <th className="px-4 py-2 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {appData.result_schema.map((field, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 font-mono text-xs">{field.name}</td>
                        <td className="px-4 py-2">{field.type}</td>
                        <td className="px-4 py-2">
                          {field.required ? (
                            <span className="text-red-600">Yes</span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                        <td className="px-4 py-2">{toDisplayString(field.unit) || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">{toDisplayString(field.description) || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Requirements Card */}
          {appData.requirements && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Min FW Version</dt>
                  <dd className="font-medium">{appData.requirements.min_firmware_version || 'Any'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Max FW Version</dt>
                  <dd className="font-medium">{appData.requirements.max_firmware_version || 'Any'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Min RAM</dt>
                  <dd className="font-medium">
                    {appData.requirements.min_ram_mb ? `${appData.requirements.min_ram_mb} MB` : 'Not specified'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Min Disk</dt>
                  <dd className="font-medium">
                    {appData.requirements.min_disk_mb ? `${appData.requirements.min_disk_mb} MB` : 'Not specified'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Requires IMU</dt>
                  <dd>{appData.requirements.requires_imu ? 'Yes' : 'No'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Requires Tensometers</dt>
                  <dd>{appData.requirements.requires_tensometers ? 'Yes' : 'No'}</dd>
                </div>
              </dl>
            </div>
          )}

          {/* Timeline Card */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Created</dt>
                <dd>{formatDate(appData.created_at)}</dd>
              </div>
              {appData.submitted_at && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Submitted</dt>
                  <dd>{formatDate(appData.submitted_at)}</dd>
                </div>
              )}
              {appData.reviewed_at && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Reviewed</dt>
                  <dd>{formatDate(appData.reviewed_at)}</dd>
                </div>
              )}
              {appData.reviewed_by_name && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Reviewed By</dt>
                  <dd>{appData.reviewed_by_name}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Image Digests Card */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Image Digests</h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Current Digest</dt>
                <dd className="font-mono">{shortDigest(appData.current_image_digest) || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Approved Digest</dt>
                <dd className="font-mono">{shortDigest(appData.approved_image_digest) || '-'}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Configuration Section (issue #20) */}
      <AppConfigCard appId={id!} appSlug={appData.app_id} />

      {/* Activity Section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Activity Log
            {reviews?.data && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({reviews.data.length})
              </span>
            )}
          </h2>
          <button
            onClick={() => setIsCommentModalOpen(true)}
            className="btn btn-secondary text-sm"
          >
            + Add Comment
          </button>
        </div>
        <ActivityTimeline reviews={reviews?.data || []} />
      </div>

      {/* Modals */}
      <RejectModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onSubmit={(reason, detailedDescription) => rejectMutation.mutate({
          reason,
          comment: detailedDescription,
        })}
        isSubmitting={rejectMutation.isPending}
        appName={appData.name}
      />

      <CommentModal
        isOpen={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        onSubmit={(comment, type) => addReviewMutation.mutate({ comment, review_type: type })}
        isSubmitting={addReviewMutation.isPending}
        isAdmin={isAdmin}
        appName={appData.name}
      />

      <AppEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        app={appData}
        onSaved={(saved) =>
          setSavedNotice(
            saved.status === 'pending_approval' ? at('savedPending') : at('savedOk'),
          )
        }
      />

      <ApproveModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onSubmit={(comment) => approveMutation.mutate(comment)}
        isSubmitting={approveMutation.isPending}
        appName={appData.name}
        wasRejected={isRejected}
        isActive={isActive}
      />

      <ActivationModal
        isOpen={activationMode !== null}
        onClose={() => {
          setActivationMode(null)
          setActivationError(null)
        }}
        mode={activationMode || 'deactivate'}
        appName={appData.name}
        isSubmitting={activationMutation.isPending}
        error={activationError}
        onSubmit={(comment) =>
          activationMutation.mutate({ mode: activationMode || 'deactivate', comment })
        }
      />

      <PromotionModal
        isOpen={promotionMode !== null}
        onClose={() => setPromotionMode(null)}
        mode={promotionMode || 'promote'}
        appId={id!}
        appName={appData.name}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ['app', id] })
          queryClient.invalidateQueries({ queryKey: ['app-reviews', id] })
          queryClient.invalidateQueries({ queryKey: ['apps'] })
        }}
      />
    </div>
  )
}

export default AppDetailPage
